import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Scale, ShieldAlert, Cpu, Crown, Workflow, DollarSign, Megaphone,
  Users, BarChart3, BookCheck, HeartHandshake, ShieldCheck, Radar,
  GraduationCap, Library, HeartPulse, MessagesSquare,
  Anchor, Stethoscope, Hammer, Plane, Factory, Sprout, School, Zap, Globe,
  ChevronRight, Sparkles, BookOpen,
} from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

const ICON_MAP = {
  Scale, ShieldAlert, Cpu, Crown, Workflow, DollarSign, Megaphone,
  Users, BarChart3, BookCheck, HeartHandshake, ShieldCheck, Radar,
  GraduationCap, Library, HeartPulse, MessagesSquare,
  Anchor, Stethoscope, Hammer, Plane, Factory, Sprout, School, Zap, Globe,
};

const TIER_LABEL = {
  leadership: 'Senior Leadership',
  specialist: 'Specialists',
  industry:   'Industry Experts',
  security:   'Security AIs',
  mentor:     'Mentors',
  concierge:  'Product Concierge',
};

export default function AIEmployees() {
  const [personas, setPersonas] = useState([]);
  const [ageMap, setAgeMap] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/ai/employees/'),
      api.get('/ai/mentors/status').catch(() => ({ data: { personas: [] } })),
    ]).then(([emp, men]) => {
      setPersonas(emp.data);
      const map = {};
      for (const c of (men.data?.personas || [])) map[c.persona] = c.age_years;
      setAgeMap(map);
    }).catch((e) => toast.error(e?.response?.data?.detail || 'Could not load AI employees.'))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="ve-section">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="ve-eyebrow flex items-center gap-2">
            <Sparkles size={14} className="text-[#8B3FC5]"/>
            AI Employees
          </div>
          <h1 className="display text-3xl md:text-4xl mt-2 font-bold">
            Twenty-six advisors. <span className="ve-gold-text">Each ages every day.</span>
          </h1>
          <p className="text-sm text-slate-600 mt-2 max-w-2xl">
            26 AI officers named after the prophets — every one seeded with 1,000 years of
            accumulated mastery in their field, growing +10 years each day under Shu&rsquo;aib&rsquo;s
            curriculum. Dawud & Harun guard the platform. Idris ingests Qatar NDS3.
            Wujdān gently corrects everyone&rsquo;s emotional tone. Iqra reads users for you.
            Bilqis advises on diplomacy and cross-border data privacy.
          </p>
        </div>
        <Link
          to="/dashboard/ai-employees/qualifications"
          data-testid="ai-view-qualifications"
          className="ve-btn-secondary inline-flex items-center gap-2 self-start md:self-end"
        >
          <BookOpen size={14}/> View Qualifications
        </Link>
      </div>

      {loading && <div className="mt-8 text-sm text-slate-500">Summoning the team…</div>}

      {['leadership', 'specialist', 'industry', 'security', 'mentor', 'concierge'].map((tier) => {
        const inTier = personas.filter((p) => (p.tier || 'leadership') === tier);
        if (!inTier.length) return null;
        return (
          <div key={tier} className="mt-8">
            <div className="ve-eyebrow text-[#5E1F8B]">{TIER_LABEL[tier]}</div>
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {inTier.map((p) => {
                const Icon = ICON_MAP[p.icon] || Sparkles;
                const age = ageMap[p.id];
                return (
                  <Link
                    key={p.id}
                    to={`/dashboard/ai-employees/${p.id}`}
                    data-testid={`ai-employee-card-${p.id}`}
                    className={`ve-card p-5 group cursor-pointer transition-all hover:border-[#8B3FC5] hover:shadow-[0_0_30px_-12px_rgba(174,82,234,0.5)] ${
                      tier === 'security' || tier === 'mentor' ? 'border-l-4 border-l-[#8B3FC5]' : ''
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="h-12 w-12 rounded-full border border-[#8B3FC5]/40 bg-black flex items-center justify-center shrink-0">
                          <Icon size={22} className="text-[#8B3FC5]"/>
                        </div>
                        <div className="min-w-0">
                          <div className="display text-xl font-bold leading-tight truncate" title={p.english_name}>{p.english_name}</div>
                          <div className="text-[18px] mt-0.5 text-[#8B3FC5] font-bold leading-tight truncate" dir="rtl" title={p.arabic_name}>
                            {p.arabic_name}
                          </div>
                        </div>
                      </div>
                      <ChevronRight size={18} className="text-slate-400 group-hover:text-[#8B3FC5] transition shrink-0"/>
                    </div>
                    <div className="mt-4 flex items-center justify-between gap-2">
                      <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold truncate" title={p.english_role}>
                        {p.english_role}
                      </div>
                      {age != null && (
                        <span
                          data-testid={`ai-employee-age-${p.id}`}
                          className="shrink-0 text-[9px] uppercase tracking-[0.18em] font-bold px-1.5 py-0.5 rounded bg-[#FFF8E5] text-[#7A5A00] dark:bg-[#3a2f0a] dark:text-[#F5E1A4]"
                        >
                          Age {Number(age).toLocaleString()}y
                        </span>
                      )}
                    </div>
                    <p className="mt-2 text-sm text-slate-700 leading-relaxed">{p.tagline}</p>
                  </Link>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
